/**
 * CLI handlers for `nexus client` commands.
 *
 * Connects to the running backend via nexus-client for manual testing.
 * Auth via Personal Access Token (PAT) stored in ~/.nexus/session.json,
 * indexed by project path for multi-project support (Docker config.json pattern).
 * Login creates a PAT "nexus-cli", logout revokes it.
 */

import { readFileSync, writeFileSync, existsSync, mkdirSync, unlinkSync } from 'node:fs'
import { join } from 'node:path'
import { homedir } from 'node:os'
import { consola } from 'consola'
import { getProjectPath } from '@kernel/config/paths.js'

// ── Session file (~/.nexus/session.json) ────────────────────────────────────

const NEXUS_DIR = join(homedir(), '.nexus')
const SESSION_FILE = join(NEXUS_DIR, 'session.json')

interface SessionEntry {
  baseURL: string
  accessToken: string
  tokenId: string
  instanceId?: string
}

type SessionStore = Record<string, SessionEntry>

function loadStore(): SessionStore {
  if (!existsSync(SESSION_FILE)) return {}
  try {
    return JSON.parse(readFileSync(SESSION_FILE, 'utf-8'))
  } catch {
    return {}
  }
}

function saveStore(store: SessionStore): void {
  if (!existsSync(NEXUS_DIR)) mkdirSync(NEXUS_DIR, { recursive: true })
  if (Object.keys(store).length === 0) {
    if (existsSync(SESSION_FILE)) unlinkSync(SESSION_FILE)
    return
  }
  writeFileSync(SESSION_FILE, JSON.stringify(store, null, 2), { mode: 0o600 })
}

export function getSession(projectPath: string): SessionEntry | null {
  const store = loadStore()
  return store[projectPath] ?? null
}

function setSession(projectPath: string, entry: SessionEntry): void {
  const store = loadStore()
  store[projectPath] = entry
  saveStore(store)
}

function removeSession(projectPath: string): void {
  const store = loadStore()
  delete store[projectPath]
  saveStore(store)
}

/** Storage adapter that reads the PAT for a specific project */
function createFileStorage(projectPath: string) {
  const session = getSession(projectPath)
  const data: Record<string, string> = session ? { accessToken: session.accessToken } : {}
  return {
    getItem: (k: string) => data[k] ?? null,
    setItem: (k: string, v: string) => { data[k] = v },
    removeItem: (k: string) => { delete data[k] },
    clear: () => { for (const k in data) delete data[k] },
    get length() { return Object.keys(data).length },
    key: (index: number) => Object.keys(data)[index] ?? null,
  }
}

export type ClientOptions = { debug?: boolean; remote?: boolean }

// ── Bootstrap ───────────────────────────────────────────────────────────────

function getLocalURL(): string {
  const port = process.env['PORT'] || '3000'
  return `http://localhost:${port}/api/v1`
}

function getTunnelURL(): string | null {
  const subdomain = process.env['FRPC_SUBDOMAIN']
  const server = process.env['FRPC_SERVER']
  const publicHost = process.env['FRPC_PUBLIC_HOST'] ?? server
  if (subdomain && publicHost) return `https://${subdomain}.${publicHost}/api/v1`
  return null
}

async function probeHealth(url: string): Promise<boolean> {
  try {
    const res = await fetch(`${url}/health`, { signal: AbortSignal.timeout(2000) })
    return res.ok
  } catch {
    return false
  }
}

async function fetchInstanceId(baseURL: string): Promise<string | null> {
  try {
    // baseURL is like http://localhost:3000/api/v1 — health is at /api/health
    const origin = baseURL.replace(/\/api\/v1$/, '')
    const res = await fetch(`${origin}/api/health`, { signal: AbortSignal.timeout(2000) })
    if (!res.ok) return null
    const data = await res.json() as { instanceId?: string }
    return data.instanceId ?? null
  } catch {
    return null
  }
}

/**
 * Auto-detect which backend is reachable.
 * --remote forces tunnel. Otherwise: try localhost first, fallback to tunnel.
 */
async function resolveBaseURL(remote?: boolean): Promise<string> {
  if (remote) {
    const tunnel = getTunnelURL()
    if (tunnel) return tunnel
    consola.warn('No FRPC config found, falling back to localhost')
  }

  const local = getLocalURL()
  if (await probeHealth(local)) return local

  const tunnel = getTunnelURL()
  if (tunnel && await probeHealth(tunnel)) {
    consola.info('Local backend not running, using tunnel')
    return tunnel
  }

  // Return local anyway — createClient will show connection error
  return local
}

export async function createClient(options?: ClientOptions) {
  const { createNexusClient } = await import('@gzl10/nexus-client')
  const baseURL = await resolveBaseURL(options?.remote)
  const projectPath = getProjectPath()
  consola.info(`Project: ${projectPath}`)
  consola.info(`Connecting to: ${baseURL}`)

  // Validate instanceId matches (detect backend change)
  const session = getSession(projectPath)
  if (session?.instanceId) {
    const remoteId = await fetchInstanceId(baseURL)
    if (remoteId && remoteId !== session.instanceId) {
      consola.warn(`Backend changed (expected ${session.instanceId.slice(0, 8)}..., got ${remoteId.slice(0, 8)}...)`)
      consola.warn('Session invalidated. Run `nexus client login` again.')
      removeSession(projectPath)
      process.exit(0)
    }
  }

  const nexus = await createNexusClient({
    baseUrl: baseURL,
    storage: createFileStorage(projectPath),
    debug: options?.debug ?? false,
  })

  return { nexus, baseURL, projectPath }
}

/** Disconnect socket and exit cleanly */
export function cleanup(nexus: { socket?: { disconnect: () => void } | null }, code = 0): never {
  nexus.socket?.disconnect()
  process.exit(code)
}

/** Check if error is a connection failure */
export function isConnectionError(err: any): boolean {
  const code = err.code || err.cause?.code
  return code === 'ECONNREFUSED' || code === 'ENOTFOUND' || code === 'ECONNRESET'
}

export function handleConnectionError(): never {
  const url = resolveBaseURL()
  consola.error(`Backend not reachable at ${url}`)
  consola.info('Start it with: pnpm dev (development) or pnpm start (production)')
  process.exit(1)
}

// ── nexus client health ─────────────────────────────────────────────────────

export async function handleClientHealth(options: ClientOptions): Promise<void> {
  const { nexus } = await createClient(options)
  try {
    const result = await nexus.health()
    console.log(JSON.stringify(result, null, 2))
    cleanup(nexus)
  } catch (err: any) {
    if (isConnectionError(err)) handleConnectionError()
    consola.error(`Health check failed: ${err.message}`)
    cleanup(nexus, 1)
  }
}

// ── nexus client login ──────────────────────────────────────────────────────

export async function handleClientLogin(
  email: string | undefined,
  password: string | undefined,
  options: ClientOptions
): Promise<void> {
  const projectPath = getProjectPath()

  const existing = getSession(projectPath)
  if (existing) {
    // Logout existing session silently before re-login
    try {
      const { nexus: oldClient } = await createClient(options)
      await oldClient.auth.logoutPAT()
      oldClient.socket?.disconnect()
    } catch {
      // If revoke fails (expired, server down), just clear local session
    }
    removeSession(projectPath)
  }

  const { nexus, baseURL } = await createClient(options)
  const finalEmail = email || process.env['ADMIN_EMAIL'] || 'admin@example.com'
  const finalPassword = password || process.env['ADMIN_PASSWORD'] || 'admin123'

  consola.info(`Logging in as: ${finalEmail}`)

  try {
    const result = await nexus.auth.loginWithPAT(finalEmail, finalPassword, {
      tokenName: 'nexus-cli',
      scope: 'readwrite',
      expiresInDays: 30,
    })

    const instanceId = await fetchInstanceId(baseURL)
    setSession(projectPath, { baseURL, accessToken: result.token, tokenId: result.tokenId, instanceId: instanceId ?? undefined })

    consola.success(`Logged in as: ${result.user.email}`)
    console.log(JSON.stringify({
      id: result.user.id,
      email: result.user.email,
      name: result.user.name,
      roles: result.user.roles,
      token: `${result.token.slice(0, 12)}...`,
    }, null, 2))
    cleanup(nexus)
  } catch (err: any) {
    if (isConnectionError(err)) handleConnectionError()
    const apiError = err.response?.data?.error
    consola.error(apiError ? `Login failed: ${apiError.message}` : `Login failed: ${err.message}`)
    cleanup(nexus, 1)
  }
}

// ── nexus client me ─────────────────────────────────────────────────────────

export async function handleClientMe(options: ClientOptions): Promise<void> {
  const projectPath = getProjectPath()
  const session = getSession(projectPath)
  if (!session) {
    consola.error('Not logged in. Run `nexus client login` first.')
    process.exit(1)
  }

  const { nexus } = await createClient(options)
  try {
    const { user } = await nexus.auth.me()
    console.log(JSON.stringify(user, null, 2))
    cleanup(nexus)
  } catch (err: any) {
    if (isConnectionError(err)) handleConnectionError()
    consola.error(`Failed: ${err.message}`)
    cleanup(nexus, 1)
  }
}

// ── nexus client logout ─────────────────────────────────────────────────────

export async function handleClientLogout(options: ClientOptions): Promise<void> {
  const projectPath = getProjectPath()
  const session = getSession(projectPath)
  if (!session) {
    consola.warn('Not logged in.')
    process.exit(0)
  }

  const { nexus } = await createClient(options)
  await nexus.auth.logoutPAT()
  removeSession(projectPath)
  consola.success('Logged out (PAT revoked)')
  cleanup(nexus)
}


// ── nexus client entity ─────────────────────────────────────────────────────

export async function handleClientEntity(
  path: string,
  action: string,
  args: string[],
  options: ClientOptions
): Promise<void> {
  const projectPath = getProjectPath()
  const session = getSession(projectPath)
  if (!session) {
    consola.error('Not logged in. Run `nexus client login` first.')
    process.exit(1)
  }

  const { nexus } = await createClient(options)
  const entity = nexus.entity(path)

  try {
    switch (action) {
      case 'list': {
        const result = await entity.list()
        console.log(JSON.stringify(result, null, 2))
        break
      }
      case 'get': {
        if (!args[0]) { consola.error('Missing id'); cleanup(nexus, 1) }
        const item = await entity.get(args[0])
        console.log(JSON.stringify(item, null, 2))
        break
      }
      case 'create': {
        if (!args[0]) { consola.error('Missing JSON body'); cleanup(nexus, 1) }
        const created = await entity.create(JSON.parse(args[0]))
        console.log(JSON.stringify(created, null, 2))
        break
      }
      case 'update': {
        if (!args[0] || !args[1]) { consola.error('Missing id or JSON body'); cleanup(nexus, 1) }
        const updated = await entity.update(args[0], JSON.parse(args[1]))
        console.log(JSON.stringify(updated, null, 2))
        break
      }
      case 'delete': {
        if (!args[0]) { consola.error('Missing id'); cleanup(nexus, 1) }
        const deleted = await entity.delete(args[0])
        if (deleted) {
          consola.success('Deleted')
        } else {
          consola.warn('Not found (already deleted or endpoint not available)')
          cleanup(nexus, 1)
        }
        break
      }
      default:
        consola.error(`Unknown action: ${action}. Use: list, get, create, update, delete`)
        cleanup(nexus, 1)
    }
    cleanup(nexus)
  } catch (err: any) {
    if (isConnectionError(err)) handleConnectionError()
    const apiError = err.response?.data?.error
    if (apiError) {
      consola.error(`API Error: ${apiError.message || apiError.code}`)
      if (err.response?.data?.details) {
        console.error('Details:', JSON.stringify(err.response.data.details, null, 2))
      }
    } else {
      consola.error(`Error: ${err.message}`)
    }
    cleanup(nexus, 1)
  }
}

// ── nexus client fetch ─────────────────────────────────────────────────────

export async function handleClientFetch(
  method: string,
  url: string,
  args: string[],
  options: ClientOptions & { pat?: string }
): Promise<void> {
  const projectPath = getProjectPath()

  // --pat: create client with inline PAT instead of session
  if (options.pat) {
    const { createNexusClient } = await import('@gzl10/nexus-client')
    const baseURL = await resolveBaseURL(options.remote)
    consola.info(`Connecting to: ${baseURL}`)

    const inlineStorage = {
      getItem: (k: string) => k === 'accessToken' ? options.pat! : null,
      setItem: () => {},
      removeItem: () => {},
      clear: () => {},
      get length() { return 1 },
      key: () => null,
    }

    const nexus = await createNexusClient({
      baseUrl: baseURL,
      storage: inlineStorage,
      debug: options.debug ?? false,
    })

    return executeFetch(nexus, method, url, args)
  }

  // Normal session-based auth
  const session = getSession(projectPath)
  if (!session) {
    consola.error('Not logged in. Run `nexus client login` first, or use --pat <token>.')
    process.exit(1)
  }

  const { nexus } = await createClient(options)
  return executeFetch(nexus, method, url, args)
}

async function executeFetch(
  nexus: { http: any; socket?: { disconnect: () => void } | null },
  method: string,
  url: string,
  args: string[]
): Promise<void> {
  const verb = method.toUpperCase()

  // Parse optional JSON body from args
  let body: Record<string, unknown> | undefined
  for (const arg of args) {
    if (arg.startsWith('{') || arg.startsWith('[')) {
      body = JSON.parse(arg)
      break
    }
  }

  // Normalize path: ensure leading slash, strip /api/v1 prefix if user included it
  let path = url.startsWith('/') ? url : `/${url}`
  path = path.replace(/^\/api\/v1/, '')

  try {
    let result: unknown
    switch (verb) {
      case 'GET':
        result = await nexus.http.get(path, { params: body })
        break
      case 'DELETE':
        result = await nexus.http.delete(path, { data: body })
        break
      case 'PUT':
        result = await nexus.http.put(path, body)
        break
      case 'PATCH':
        result = await nexus.http.patch(path, body)
        break
      default:
        result = await nexus.http.post(path, body)
    }
    if (result !== undefined && result !== null && result !== '') {
      console.log(JSON.stringify(result, null, 2))
    }
    cleanup(nexus, 0)
  } catch (err: any) {
    if (isConnectionError(err)) handleConnectionError()
    const data = err.response?.data
    if (data) {
      console.log(JSON.stringify(data, null, 2))
    } else {
      consola.error(`Failed: ${err.message}`)
    }
    cleanup(nexus, 1)
  }
}

// ── nexus client action ────────────────────────────────────────────────────

export async function handleClientAction(
  module: string,
  actionKey: string,
  args: string[],
  options: ClientOptions & { method?: string }
): Promise<void> {
  const projectPath = getProjectPath()
  const session = getSession(projectPath)
  if (!session) {
    consola.error('Not logged in. Run `nexus client login` first.')
    process.exit(1)
  }

  const { nexus } = await createClient(options)
  const method = (options.method ?? 'POST').toUpperCase()

  // Parse args: [id] [json] or [json]
  let id: string | undefined
  let body: Record<string, unknown> | undefined

  for (const arg of args) {
    if (arg.startsWith('{') || arg.startsWith('[')) {
      body = JSON.parse(arg)
    } else if (!id) {
      id = arg
    }
  }

  // Build URL path: /{module}/{action-key}[/{id}]
  const path = id ? `/${module}/${actionKey}/${id}` : `/${module}/${actionKey}`

  try {
    let result: unknown
    switch (method) {
      case 'GET':
        result = await nexus.http.get<unknown>(path, { params: body })
        break
      case 'DELETE':
        result = await nexus.http.delete<unknown>(path, { data: body })
        break
      case 'PUT':
        result = await nexus.http.put<unknown>(path, body)
        break
      default:
        result = await nexus.http.post<unknown>(path, body)
    }
    if (result !== undefined && result !== null && result !== '') {
      console.log(JSON.stringify(result, null, 2))
    }
    cleanup(nexus)
  } catch (err: any) {
    if (isConnectionError(err)) handleConnectionError()
    const data = err.response?.data
    if (data) {
      console.log(JSON.stringify(data, null, 2))
    } else {
      consola.error(`Failed: ${err.message}`)
    }
    cleanup(nexus, 1)
  }
}
